import { useTranslation } from 'next-i18next'
import { observer } from 'mobx-react-lite'
import type { Swiper as SwiperType } from 'swiper'
import { useRouter } from 'next/router'
import { useState, useRef, useMemo, useEffect } from 'react'
import { getTopList } from 'api/price'
import type { TopListData } from 'api-price'
import { stopEvent, toScrollEle } from '@/utils/tools'
import { Flex } from '@mantine/core'
import useLangPrefix from '@/hooks/useLangPrefix'
import { useDebounceFn, useRequest, useMemoizedFn } from 'ahooks'
import { Tabs } from '@gate/gui'
import { useAdapter } from '@/components/Adapter'
import { HOME_PAGE_URL, COIN_TRADE } from '@/constants/index'
import BaseEmpty from '@/components/BaseEmpty'
import CoinListItem from '@/blocks/Price/PriceRec/CoinList/Item'
import { useStore } from '@/store/index'
import { Swiper, SwiperSlide } from 'swiper/react'
import useWebSocket from '@/hooks/useWebSocket'
import useWsUrl, { getId } from '@/hooks/useWsUrl'
import { GateProIconaV3114PxLineYoujiantou, GateProIconaV3114PxLineZuojiantou } from '@gate/iconfont'
import {
  EnumCoinTopTabs,
  EnumCoinTopUrlTabs,
  recordTabsMap,
  recordMoreMap,
  recordFlipPageMap,
} from '@/blocks/Price/PriceRec/CoinList/data'
import {
  CoinListStyled,
  CoinMoreStyled,
  CoinListSwiperStyled,
  CoinListSwiperCardStyled,
  CoinListSwiperActionStyled,
  CoinListSwiperButton,
  CoinListSwiperRightButton,
} from './style'
import { eventTracker } from '@/utils/collectEvent'
import useHistoryRouter from '@/hooks/useHistoryRouter'

const CoinList = () => {
  const { locale } = useRouter()
  const { langPrefix } = useLangPrefix()
  const { t } = useTranslation()
  const { isMd } = useAdapter()
  const { wsUrl } = useWsUrl()
  const { historyRouter } = useHistoryRouter()

  const swiperRef = useRef<SwiperType | null>(null)
  const coinTabsRefs = useRef<HTMLDivElement | null>(null)
  const [tab, setTab] = useState<EnumCoinTopTabs>(EnumCoinTopTabs.HOT)
  const [isOverflowX, setIsOverflowX] = useState(false)

  const {
    globalStore: { isDark },
    baseStore,
  } = useStore()

  const {
    priceHotList,
    priceClientData: { priceNewCoinList, priceGainsCoinList },
    sePriceRecommendList,
    setTitleTab,
  } = baseStore

  const currentCoinList = {
    [EnumCoinTopTabs.HOT]: priceHotList?.list,
    [EnumCoinTopTabs.NEW]: priceNewCoinList?.list,
    [EnumCoinTopTabs.GAINS]: priceGainsCoinList?.list,
  }

  const initCoinList = {
    [EnumCoinTopTabs.HOT]: priceHotList,
    [EnumCoinTopTabs.NEW]: priceNewCoinList,
    [EnumCoinTopTabs.GAINS]: priceGainsCoinList,
  }

  const { run } = useRequest(getTopList, {
    manual: true,
    onSuccess(data) {
      handleSendMassage(
        data?.list?.map((item: any) => `${item?.coin_short_name?.toLocaleUpperCase()}_${COIN_TRADE}`) ?? [],
        'ticker.subscribe'
      )

      sePriceRecommendList(data as TopListData, tab)
    },
  })

  const onMessage = useMemoizedFn(message => {
    if (message?.method === 'ticker.update' && message?.params?.[0]) {
      const id = message?.params?.[0]
      const newItem = message?.params?.[1]

      const currentData = initCoinList[tab]?.list?.map(item => {
        if (`${item?.coin_short_name}_${COIN_TRADE}` === id) {
          return {
            ...item,
            dimension_24h: newItem?.change,
          }
        }
        return item
      })

      sePriceRecommendList(
        {
          ...initCoinList[tab],
          list: currentData,
        } as TopListData,
        tab
      )
    }
  })

  const { isOpen, sendMassage } = useWebSocket(wsUrl, {
    onMessage,
  })

  //  todo ws 切换参数
  const handleSendMassage = useMemoizedFn((params: string[], method = 'ticker.set_subscription_change_timezone') => {
    if (isOpen) {
      sendMassage({
        id: getId(),
        method,
        params,
      })
    }
  })

  useEffect(() => {
    if (isOpen) {
      const currentList = currentCoinList[tab]
      handleSendMassage(
        currentList?.map((item: any) => `${item?.coin_short_name?.toLocaleUpperCase()}_${COIN_TRADE}`) ?? [],
        'ticker.subscribe'
      )
    }
  }, [isOpen, tab])

  const leftDisabled = useMemo<boolean>(() => {
    if (tab === EnumCoinTopTabs.HOT) {
      return (priceHotList?.page ?? 0) <= 1
    }
    if (tab === EnumCoinTopTabs.NEW) {
      return (priceNewCoinList?.page ?? 0) <= 1
    }
    if (tab === EnumCoinTopTabs.GAINS) {
      return (priceGainsCoinList?.page ?? 0) <= 1
    }
    return false
  }, [priceGainsCoinList?.page, priceHotList?.page, priceNewCoinList?.page, tab])

  const rightDisabled = useMemo<boolean>(() => {
    if (tab === EnumCoinTopTabs.HOT) {
      return (
        (priceHotList?.page ?? 0) * 3 >= ((priceHotList?.totalCount ?? 0) >= 50 ? 50 : (priceHotList?.totalCount ?? 0))
      )
    }
    if (tab === EnumCoinTopTabs.NEW) {
      return (
        (priceNewCoinList?.page ?? 0) * 3 >=
        ((priceNewCoinList?.totalCount ?? 0) >= 50 ? 50 : (priceNewCoinList?.totalCount ?? 0))
      )
    }
    if (tab === EnumCoinTopTabs.GAINS) {
      return (
        (priceGainsCoinList?.page ?? 0) * 3 >=
        ((priceGainsCoinList?.totalCount ?? 0) >= 50 ? 50 : (priceGainsCoinList?.totalCount ?? 0))
      )
    }
    return false
  }, [
    priceGainsCoinList?.page,
    priceGainsCoinList?.totalCount,
    priceHotList?.page,
    priceHotList?.totalCount,
    priceNewCoinList?.page,
    priceNewCoinList?.totalCount,
    tab,
  ])

  /**
   * @description: 翻页
   * @return {*}
   */
  const { run: onPageChange } = useDebounceFn(
    (type?: '+' | '-') => {
      let page = 1
      if (tab === EnumCoinTopTabs.HOT) {
        page = priceHotList?.page ?? 1
      }
      if (tab === EnumCoinTopTabs.NEW) {
        page = priceNewCoinList?.page ?? 1
      }
      if (tab === EnumCoinTopTabs.GAINS) {
        page = priceGainsCoinList?.page ?? 1
      }

      eventTracker('price_overview_click', {
        button_name: recordFlipPageMap[tab],
      })

      if (type === '+') {
        page++
      }
      if (type === '-') {
        page--
      }

      run({ page, pageSize: 3 }, tab)
    },
    { wait: 500 }
  )

  /**
   * @description: 查看更多
   * @return {*}
   */
  const onMoreClick = async () => {
    setTitleTab(HOME_PAGE_URL.Market)
    toScrollEle('table', 0)
    eventTracker('price_overview_click', {
      button_name: recordMoreMap[tab],
    })
    historyRouter({
      type: 'price',
      tab: `/price${EnumCoinTopUrlTabs[tab]}` as HOME_PAGE_URL,
    })
  }

  useEffect(() => {
    if (coinTabsRefs?.current) {
      if (coinTabsRefs?.current?.scrollWidth > coinTabsRefs?.current?.clientWidth) {
        setIsOverflowX(true)
      }
    }
  }, [])

  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev()
      onPageChange('-')
    }
  }

  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext()
      onPageChange('+')
    }
  }

  return (
    <CoinListStyled isDark={isDark}>
      <Flex align="center" justify="space-between" gap={'8px'}>
        <Tabs
          size={'xs'}
          type="primary-no-underline"
          value={tab}
          onTabChange={(tab: EnumCoinTopTabs) => {
            eventTracker('price_overview_click', {
              tab_name: recordTabsMap[tab],
            })
            setTab(tab)
          }}
          styles={{
            root: {
              overflowX: 'auto',
              '&::-webkit-scrollbar': { width: 0, height: 0 },
            },
            tabsList: { flexWrap: 'nowrap' },
          }}
        >
          <Tabs.List ref={coinTabsRefs}>
            {Object.values(EnumCoinTopTabs).map(key => (
              <Tabs.Tab key={key} value={key}>
                <Flex align="center" className="font-700 size-20-16" gap={4}>
                  {key === EnumCoinTopTabs.HOT ? (
                    <svg className="iconpark-icon" style={{ width: 12, height: 12 }}>
                      <use href="#v31-12px-Hot"></use>
                    </svg>
                  ) : null}
                  {t(`P.${key}`)}
                </Flex>
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>

        <CoinMoreStyled
          align={'center'}
          data-ar={String(locale === 'ar')}
          gap={2}
          {...{ component: 'a', href: `${langPrefix}/price${EnumCoinTopUrlTabs[tab]}` }}
          onClick={e => {
            stopEvent(e)
            onMoreClick()
          }}
          sx={{
            position: 'relative',
            '&:after': isOverflowX
              ? {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-20px',
                  width: '16px',
                  height: '100%',
                  background: isDark
                    ? `linear-gradient(to left, rgb(29 29 36 / 100%), rgb(29 29 36 / 10%))`
                    : `linear-gradient(to left, rgb(255 255 255 / 100%), rgb(255 255 255 / 10%))`,
                  pointerEvents: 'none',
                }
              : {},
          }}
        >
          <GateProIconaV3114PxLineYoujiantou />
        </CoinMoreStyled>
      </Flex>
      {Object.values(EnumCoinTopTabs).map(key => {
        return (
          <Flex
            key={key}
            style={tab === key ? {} : { display: 'none' }}
            justify="space-between"
            direction="column"
            h={'calc(100% - 48px)'}
          >
            {currentCoinList[key]?.length ? (
              <CoinListSwiperStyled>
                <Swiper centeredSlides onSwiper={swiper => (swiperRef.current = swiper)} spaceBetween={50}>
                  <SwiperSlide>
                    <CoinListSwiperCardStyled>
                      {currentCoinList[key]?.map(item => (
                        <CoinListItem
                          currentTabs={key}
                          key={item.coin_short_name || item?.gate_id}
                          {...item}
                          onPageChange={onPageChange}
                        />
                      ))}
                    </CoinListSwiperCardStyled>
                  </SwiperSlide>
                </Swiper>

                {isMd ? (
                  <CoinListSwiperActionStyled className="coin-list-swiper-box">
                    <CoinListSwiperButton
                      isHide={leftDisabled}
                      onClick={handlePrev}
                      type="secondary"
                      disabled={leftDisabled}
                      size="xs"
                      leftIcon={<GateProIconaV3114PxLineZuojiantou />}
                      rtlIcon={<GateProIconaV3114PxLineYoujiantou />}
                    />
                    <CoinListSwiperRightButton
                      onClick={handleNext}
                      isHide={rightDisabled}
                      type="secondary"
                      disabled={rightDisabled}
                      size="xs"
                      leftIcon={<GateProIconaV3114PxLineYoujiantou />}
                      rtlIcon={<GateProIconaV3114PxLineZuojiantou />}
                    />
                  </CoinListSwiperActionStyled>
                ) : null}
              </CoinListSwiperStyled>
            ) : (
              <Flex h={150} align="center">
                <BaseEmpty size="xs" />
              </Flex>
            )}
          </Flex>
        )
      })}
    </CoinListStyled>
  )
}

export default observer(CoinList)
